import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { PageHeader, Spinner, EmptyState, useToast, SearchableSelect, Badge, statusBadgeMap, useSort, SortTh, usePagination, Pagination, InlineSpinner, TableSkeletonRows, DateRangePicker } from '../components/UI';
import { usePermissions } from '../context/PermissionsContext';
import { fmtDate, fmtPct } from '../utils/format';

const TIMELINE_ICON = {
  academic:     'bi-graph-up',
  disciplinary: 'bi-exclamation-circle',
  log_activity: 'bi-journal-text',
};

const getPctColor = pct => pct >= 75 ? 'var(--sms-dot-present)' : pct >= 50 ? 'var(--sms-dot-late)' : 'var(--sms-dot-absent)';

const AT_RISK_DEFINITION = 'A student is flagged At Risk if attendance is below 75%, academic average is below 50%, or they have an open (unresolved) disciplinary case.';

function AtRiskBadge({ atRisk, reasons }) {
  if (!atRisk) return null;
  const title = reasons && reasons.length ? reasons.join('; ') : AT_RISK_DEFINITION;
  return (
    <span className="badge rounded-pill badge-at-risk" title={title} style={{ fontSize: 12, cursor: 'help' }}>
      <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: 12, marginRight: 3 }} />At Risk
    </span>
  );
}

function InfoHint({ text }) {
  return (
    <i className="bi bi-info-circle" title={text}
      style={{ fontSize: 12, color: 'var(--sms-txt3)', marginLeft: 5, cursor: 'help' }} />
  );
}

// Same gradient-icon stat card the dashboard uses (.sms-stat-card), reused
// here so this page's summary numbers don't read as flat/plain — pair with
// the .sms-stat-anim-row wrapper for the staggered fade-in.
function ReportStatCard({ label, value, icon, color, sub, hint }) {
  return (
    <div className="sms-stat-card h-100" style={{
      position: 'relative', overflow: 'hidden',
      background: `linear-gradient(180deg, color-mix(in srgb, ${color} 7%, var(--sms-sur)), var(--sms-sur) 65%)`,
    }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}{hint && <InfoHint text={hint} />}
        </span>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: `color-mix(in srgb, ${color} 14%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className={`bi ${icon}`} style={{ color, fontSize: 16 }} />
        </div>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--sms-txt)', lineHeight: 1.1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function StudentView() {
  const toast = useToast();
  const { can } = usePermissions();
  const [students, setStudents] = useState([]);
  const [studentNo, setStudentNo] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Which of the student's enrolments the numbers are for ('' = primary).
  const [intakeCode, setIntakeCode] = useState('');
  const [switching, setSwitching] = useState(false);

  useEffect(() => { api.get('/students').then(r => setStudents(r.data.data)).catch(() => {}); }, []);

  useEffect(() => { setIntakeCode(''); }, [studentNo]);

  useEffect(() => {
    if (!studentNo) { setReport(null); return; }
    // Switching intake keeps the current report on screen (dimmed) so the cards
    // can transition instead of the whole page flashing a spinner.
    const sameStudent = report?.student.student_no === studentNo;
    if (sameStudent) setSwitching(true); else setLoading(true);
    let cancelled = false;
    api.get(`/reports/progression/${studentNo}`, { params: intakeCode ? { intake_code: intakeCode } : {} })
      .then(r => { if (!cancelled) setReport(r.data.data); })
      .catch(() => { if (!cancelled) { toast('Failed to load report', 'error'); setReport(null); } })
      .finally(() => { if (!cancelled) { setLoading(false); setSwitching(false); } });
    return () => { cancelled = true; };
  }, [studentNo, intakeCode]);

  const enrolments = report?.enrolments || [];
  const activeIntake = report?.selected_intake || '';
  const moveIntake = step => {
    const i = enrolments.findIndex(e => e.intake_code === activeIntake);
    const next = enrolments[(i + step + enrolments.length) % enrolments.length];
    if (next) setIntakeCode(next.intake_code);
  };

  const handleExport = async () => {
    if (!studentNo) return;
    setExporting(true);
    try {
      const res = await api.get(`/reports/progression/${studentNo}/export`, { responseType: 'blob', params: intakeCode ? { intake_code: intakeCode } : {} });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `progression_${studentNo}${activeIntake ? `_${activeIntake}` : ''}.xlsx`;
      a.click();
    } catch {
      toast('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader title="Student Progression Report" subtitle={report ? report.student.name : 'Select a student to view their progression'}
        actions={report && can('reports','view') && (
          <button className="sms-btn sms-btn-primary" onClick={handleExport} disabled={exporting}>
            {exporting ? <><InlineSpinner style={{ marginRight: 4 }} />Exporting…</> : <><i className="bi bi-file-earmark-excel" /> Export to Excel</>}
          </button>
        )} />

      <div className="sms-card mb-3" style={{ padding: '12px 14px' }}>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-6">
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 4 }}>Student</label>
            <SearchableSelect value={studentNo} onChange={e => setStudentNo(e.target.value)} placeholder="-- Select student --"
              options={students.map(s => ({ value: s.student_no, label: s.name, right: s.student_no }))} />
          </div>
        </div>
      </div>

      {loading ? <Spinner minHeight="240px" /> : !studentNo ? (
        <EmptyState icon="bi-graph-up-arrow" message="Select a student to view their progression report" />
      ) : !report ? (
        <EmptyState icon="bi-exclamation-triangle" message="No data found for this student" />
      ) : (
        <>
          <div className="sms-card mb-3" style={{ padding: '14px 16px' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{report.student.name}</div>
                <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                  {report.student.student_no} &middot; {report.student.intake || '—'}
                </div>
              </div>
              {enrolments.length > 1 && (
                <div className="prog-intake-switch" role="tablist" aria-label="Intake for this report"
                  onKeyDown={e => {
                    if (e.key === 'ArrowRight') { e.preventDefault(); moveIntake(1); }
                    if (e.key === 'ArrowLeft') { e.preventDefault(); moveIntake(-1); }
                  }}>
                  <button type="button" className="prog-intake-arrow" aria-label="Previous intake" onClick={() => moveIntake(-1)}>
                    <i className="bi bi-chevron-left" aria-hidden="true" />
                  </button>
                  {enrolments.map(e => (
                    <button key={e.intake_code} type="button" role="tab" aria-selected={e.intake_code === activeIntake}
                      tabIndex={e.intake_code === activeIntake ? 0 : -1}
                      className={`prog-intake-card${e.intake_code === activeIntake ? ' active' : ''}`}
                      onClick={() => setIntakeCode(e.intake_code)}>
                      <span className="prog-intake-code">{e.intake_code}</span>
                      <span className="prog-intake-meta">
                        {e.is_primary ? 'Primary' : 'Additional'} &middot; {e.status ? e.status.charAt(0).toUpperCase() + e.status.slice(1) : '—'}
                      </span>
                    </button>
                  ))}
                  <button type="button" className="prog-intake-arrow" aria-label="Next intake" onClick={() => moveIntake(1)}>
                    <i className="bi bi-chevron-right" aria-hidden="true" />
                  </button>
                </div>
              )}
              <div className="d-flex align-items-center gap-2">
                <AtRiskBadge atRisk={report.summary.at_risk} reasons={report.summary.at_risk_reasons} />
                <Badge value={report.student.status} map={statusBadgeMap} />
              </div>
            </div>
            {report.summary.at_risk && report.summary.at_risk_reasons?.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginTop: 8 }}>
                {report.summary.at_risk_reasons.join(' · ')}
              </div>
            )}
          </div>

          <div key={activeIntake} className={`prog-intake-body${switching ? ' is-switching' : ''}`}>
          <div className="row g-3 mb-3 sms-stat-anim-row">
            <div className="col-6 col-md-3">
              <ReportStatCard label="Academic Average" icon="bi-graph-up-arrow" color="var(--sms-stat-blue)"
                value={report.summary.academic_avg_pct != null ? `${fmtPct(report.summary.academic_avg_pct)}` : '—'} />
            </div>
            <div className="col-6 col-md-3">
              <ReportStatCard label="Attendance Rate" icon="bi-calendar-check-fill" color="var(--sms-stat-green)"
                value={report.summary.attendance_pct != null ? `${fmtPct(report.summary.attendance_pct)}` : '—'}
                sub={report.attendance.total_sessions ? `${report.attendance.total_sessions} session${report.attendance.total_sessions === 1 ? '' : 's'} · ${report.attendance.present} present · ${report.attendance.late} late · ${report.attendance.absent} absent · ${report.attendance.mc || 0} MC${report.attendance.mc > 0 ? ` (${fmtPct(report.summary.mc_pct)})` : ''}` : null} />
            </div>
            <div className="col-6 col-md-3">
              <ReportStatCard label="Disciplinary Cases" icon="bi-exclamation-triangle-fill" color="var(--sms-stat-red)"
                value={report.summary.disciplinary_count} />
            </div>
            <div className="col-6 col-md-3">
              <ReportStatCard label="Days to EOC" icon="bi-hourglass-split" color="var(--sms-stat-amber)"
                value={report.summary.days_to_eoc != null ? report.summary.days_to_eoc : '—'} />
            </div>
          </div>

          <div className="sms-card mb-3" style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Academic Performance by Subject</div>
            {report.academic_by_subject.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>No academic records yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {report.academic_by_subject.map(s => (
                  <div key={`${s.subject}-${s.intake_code}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span>{s.subject}{s.intake_code && <span style={{ color: 'var(--sms-txt3)', fontSize: 12 }}> &nbsp;{s.intake_code}</span>}</span>
                      <span style={{ color: 'var(--sms-txt2)' }}>{fmtPct(s.avg_pct)} &middot; {s.assessments} assessment{s.assessments === 1 ? '' : 's'}</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--sms-sur2)', border: '1px solid var(--sms-bdr)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${s.avg_pct}%`, background: getPctColor(s.avg_pct), borderRadius: 3 }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sms-card" style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Timeline</div>
            {report.timeline.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>No activity recorded yet.</div>
            ) : (
              <div>
                {report.timeline.map((t, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: idx < report.timeline.length - 1 ? '1px solid var(--sms-bdr)' : 'none' }}>
                    <i className={`bi ${TIMELINE_ICON[t.type] || 'bi-dot'}`} style={{ fontSize: 14, color: 'var(--sms-txt2)', marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12.5 }}>{t.description}</div>
                      <div style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>{fmtDate(t.date)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </>
      )}
    </>
  );
}

function IntakeView() {
  const toast = useToast();
  const { can } = usePermissions();
  const [intakes, setIntakes] = useState([]);
  const [intakeCode, setIntakeCode] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { api.get('/intakes').then(r => setIntakes(r.data.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!intakeCode) { setReport(null); return; }
    setLoading(true);
    api.get(`/reports/intake/${intakeCode}`)
      .then(r => setReport(r.data.data))
      .catch(() => { toast('Failed to load report', 'error'); setReport(null); })
      .finally(() => setLoading(false));
  }, [intakeCode]);

  const handleExport = async () => {
    if (!intakeCode) return;
    setExporting(true);
    try {
      const res = await api.get(`/reports/intake/${intakeCode}/export`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `progression_intake_${intakeCode}.xlsx`;
      a.click();
    } catch {
      toast('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const { sorted, sortKey, sortDir, toggleSort } = useSort(report?.students || []);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);
  const ST = (label, field, style) => <SortTh label={label} field={field} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} style={style} />;
  const RIGHT = { textAlign: 'right' };

  return (
    <>
      <PageHeader title="Intake Progression Report" subtitle={report ? `${report.intake.intake_code} · ${report.intake.student_count} students` : 'Select an intake to view its progression'}
        actions={report && can('reports','view') && (
          <button className="sms-btn sms-btn-primary" onClick={handleExport} disabled={exporting}>
            {exporting ? <><InlineSpinner style={{ marginRight: 4 }} />Exporting…</> : <><i className="bi bi-file-earmark-excel" /> Export to Excel</>}
          </button>
        )} />

      <div className="sms-card mb-3" style={{ padding: '12px 14px' }}>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-6">
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 4 }}>Intake</label>
            <SearchableSelect value={intakeCode} onChange={e => setIntakeCode(e.target.value)} placeholder="-- Select intake --"
              options={intakes.map(i => ({ value: i.intake_code, label: i.intake_code, right: `${i.student_count} students` }))} />
          </div>
        </div>
      </div>

      {!intakeCode ? (
        <EmptyState icon="bi-graph-up-arrow" message="Select an intake to view its progression report" />
      ) : !loading && !report ? (
        <EmptyState icon="bi-exclamation-triangle" message="No data found for this intake" />
      ) : (
        <>
          <div className="row g-3 mb-3 sms-stat-anim-row">
            <div className="col-6 col-md-3">
              <ReportStatCard label="Avg Academic" icon="bi-graph-up-arrow" color="var(--sms-stat-blue)"
                value={report?.summary.avg_academic_pct != null ? `${fmtPct(report.summary.avg_academic_pct)}` : '—'} />
            </div>
            <div className="col-6 col-md-3">
              <ReportStatCard label="Avg Attendance" icon="bi-calendar-check-fill" color="var(--sms-stat-green)"
                value={report?.summary.avg_attendance_pct != null ? `${fmtPct(report.summary.avg_attendance_pct)}` : '—'} />
            </div>
            <div className="col-6 col-md-3">
              <ReportStatCard label="At Risk" icon="bi-exclamation-triangle-fill" color="var(--sms-stat-red)"
                value={report?.summary.at_risk_count} hint={AT_RISK_DEFINITION} />
            </div>
            <div className="col-6 col-md-3">
              <ReportStatCard label="Students" icon="bi-people-fill" color="var(--sms-stat-violet)"
                value={report?.intake.student_count} />
            </div>
          </div>

          <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
            {!loading && report.students.length === 0 ? (
              <EmptyState icon="bi-people" message="No students in this intake" />
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="sms-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        {ST('Student No', 'student_no')}{ST('Name', 'name')}{ST('Academic Avg (%)', 'academic_avg_pct', RIGHT)}
                        {ST('Attendance (%)', 'attendance_pct', RIGHT)}{ST('Disciplinary', 'disciplinary_count')}{ST('Days to EOC', 'days_to_eoc')}
                        {ST('Status', 'status')}
                        <th style={{ width: 80 }}>At Risk<InfoHint text={AT_RISK_DEFINITION} /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? <TableSkeletonRows columns={9} /> : paged.map((s, idx) => (
                        <tr key={s.student_no}>
                          <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                          <td>{s.student_no}</td>
                          <td style={{ fontWeight: 500 }}>{s.name}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.academic_avg_pct != null ? `${fmtPct(s.academic_avg_pct)}` : '—'}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.attendance_pct != null ? `${fmtPct(s.attendance_pct)}` : '—'}</td>
                          <td>{s.disciplinary_count}</td>
                          <td>{s.days_to_eoc != null ? s.days_to_eoc : '—'}</td>
                          <td><Badge value={s.status} map={statusBadgeMap} /></td>
                          <td><AtRiskBadge atRisk={s.at_risk} reasons={s.at_risk_reasons} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

function HeadcountView() {
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const [start, setStart] = useState(yearStart);
  const [end, setEnd] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!start || !end) { setReport(null); return; }
    setLoading(true);
    api.get('/reports/headcount', { params: { start, end } })
      .then(r => setReport(r.data.data))
      .catch(() => { toast('Failed to load report', 'error'); setReport(null); })
      .finally(() => setLoading(false));
  }, [start, end]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/reports/headcount/export', { params: { start, end }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = `headcount_${start}_to_${end}.xlsx`;
      a.click();
    } catch {
      toast('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const HEAD_GROUPS = [
    { label: 'Enrolment', cols: [['Opening Balance', 'opening_balance'], ['New Enrolment', 'new_enrolment'], ['Move-In (Transfer)', 'move_in'], ['Total Students', 'total_students']] },
    { label: 'Left School', cols: [['Graduated', 'graduated'], ['Withdrawn', 'withdrawn'], ['Deferred', 'deferred'], ['Total Left', 'total_left'], ['Move-Out (Transfer)', 'move_out'], ['Total Out', 'total_out']] },
    { label: 'Current Students', cols: [['Active', 'active'], ['Registered', 'registered'], ['Closing Balance', 'total_current']] },
  ];
  const bold = new Set(['total_students', 'total_left', 'total_out', 'total_current']);
  const HEADER_HINTS = {
    total_students: 'Opening Balance + New Enrolment + Move-In',
    total_left: 'Graduated + Withdrawn + Deferred',
    move_out: 'Students transferred out of this intake',
    total_out: 'Total Left + Move-Out (Transfer)',
    total_current: 'Total Students - Total Out',
  };
  const CELL_COLOR = {
    graduated: 'var(--sms-stat-green)', withdrawn: 'var(--sms-stat-red)', deferred: 'var(--sms-stat-cyan)', move_out: 'var(--sms-stat-amber)',
    active: 'var(--sms-stat-green)', registered: 'var(--sms-stat-amber)', move_in: 'var(--sms-stat-blue)',
  };

  const Row = ({ label, r, isTotal }) => (
    <tr style={isTotal ? { fontWeight: 600, borderTop: '2px solid var(--sms-bdr)', background: 'var(--sms-sur2)' } : undefined}>
      <td className="headcount-intake-cell" style={{ fontWeight: 600 }}>{label}</td>
      {HEAD_GROUPS.flatMap((g, groupIndex) => g.cols.map(([, key], colIndex) => (
        <td key={key} className={groupIndex > 0 && colIndex === 0 ? 'headcount-group-start' : g.label === 'Left School' && key === 'move_out' ? 'headcount-subgroup-start' : undefined} style={{
          fontSize: 12, fontWeight: bold.has(key) || isTotal ? 600 : 400,
          color: CELL_COLOR[key] && r[key] > 0 ? CELL_COLOR[key] : undefined,
        }}>{r[key]}</td>
      )))}
    </tr>
  );

  return (
    <>
      <PageHeader title="Student Headcount Tracking" subtitle={report ? `${fmtDate(report.start_date)} – ${fmtDate(report.end_date)}` : 'Select a period'}
        actions={report && (
          <button className="sms-btn sms-btn-primary headcount-export-btn" onClick={handleExport} disabled={exporting}
            title="Download the current headcount report as an Excel file" aria-label="Download headcount report as Excel">
            {exporting ? <><InlineSpinner style={{ marginRight: 4 }} />Exporting…</> : <><i className="bi bi-file-earmark-spreadsheet" aria-hidden="true" /> Export to Excel</>}
          </button>
        )} />

      <div className="sms-card mb-3" style={{ padding: '12px 14px' }}>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-5">
            <label style={{ fontSize: 12, color: 'var(--sms-txt2)', display: 'block', marginBottom: 4 }}>Report Period</label>
            <DateRangePicker
              startDate={start}
              endDate={end}
              onChange={({ start: nextStart, end: nextEnd }) => {
                setStart(nextStart || '');
                setEnd(nextEnd || '');
              }}
              dayNoun="days"
              placeholder="Select report period"
            />
          </div>
        </div>
      </div>

      {!loading && !report ? (
        <EmptyState icon="bi-bar-chart-line" message="Select a period to view headcount tracking" />
      ) : !loading && report.intakes.length === 0 ? (
        <EmptyState icon="bi-people" message="No enrolment data found" />
      ) : (
        <>
          <div className="row g-3 mb-3 sms-stat-anim-row">
            <div className="col-6 col-md-3">
              <ReportStatCard label="Opening Balance" icon="bi-door-open-fill" color="var(--sms-stat-blue)" value={report?.total.opening_balance} />
            </div>
            <div className="col-6 col-md-3">
              <ReportStatCard label="Total Students" icon="bi-people-fill" color="var(--sms-stat-violet)" value={report?.total.total_students} />
            </div>
            <div className="col-6 col-md-3">
              <ReportStatCard label="Total Left" icon="bi-box-arrow-right" color="var(--sms-stat-red)" value={report?.total.total_left} />
            </div>
            <div className="col-6 col-md-3">
              <ReportStatCard label="Closing Balance" icon="bi-flag-fill" color="var(--sms-stat-green)" value={report?.total.total_current} />
            </div>
          </div>

          <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table headcount-table">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ verticalAlign: 'bottom', width: 120, minWidth: 120 }}>Intake</th>
                  {HEAD_GROUPS.map((g, i) => g.label ? (
                    <th key={i} colSpan={g.cols.length} className={i > 0 ? 'headcount-group-start' : undefined} style={{
                      textAlign: 'center',
                      color: g.label === 'Left School' ? 'var(--sms-stat-red)' : g.label === 'Current Students' ? 'var(--sms-stat-green)' : undefined,
                    }}>{g.label}</th>
                  ) : <th key={i} colSpan={g.cols.length} className={i > 0 ? 'headcount-group-start' : undefined} />)}
                </tr>
                <tr>
                  {HEAD_GROUPS.flatMap((g, groupIndex) => g.cols.map(([label, key], colIndex) => (
                    <th key={key} className={groupIndex > 0 && colIndex === 0 ? 'headcount-group-start' : g.label === 'Left School' && key === 'move_out' ? 'headcount-subgroup-start' : undefined}
                      title={HEADER_HINTS[key]} style={{ fontSize: 12 }}>
                      {label}{HEADER_HINTS[key] && <InfoHint text={HEADER_HINTS[key]} />}
                    </th>
                  )))}
                </tr>
              </thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={1 + HEAD_GROUPS.flatMap(g => g.cols).length} /> : (
                  <>
                    {report.intakes.map(r => <Row key={r.intake_code} label={r.intake_code} r={r} />)}
                    <Row label="TOTAL" r={report.total} isTotal />
                  </>
                )}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}
    </>
  );
}

export default function ProgressionReportPage() {
  const [mode, setMode] = useState('headcount');

  return (
    <>
      <div className="sms-tabs" role="tablist" aria-label="Progression report views">
        <button role="tab" aria-selected={mode === 'headcount'} className={`sms-tab${mode === 'headcount' ? ' active' : ''}`} onClick={() => setMode('headcount')}>
          <i className="bi bi-bar-chart-line" /> Headcount Tracking
        </button>
        <button role="tab" aria-selected={mode === 'student'} className={`sms-tab${mode === 'student' ? ' active' : ''}`} onClick={() => setMode('student')}>
          <i className="bi bi-person-lines-fill" /> By Student
        </button>
        <button role="tab" aria-selected={mode === 'intake'} className={`sms-tab${mode === 'intake' ? ' active' : ''}`} onClick={() => setMode('intake')}>
          <i className="bi bi-people-fill" /> By Intake
        </button>
      </div>
      {mode === 'headcount' ? <HeadcountView /> : mode === 'student' ? <StudentView /> : <IntakeView />}
    </>
  );
}
